import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Box } from "@mui/material";
import { Plus } from "lucide-react";

import { ServiceModal } from "components/Modal/ServiceModal";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { PageHeader } from "components/ui/PageHeader";
import { useCreatePracticeService } from "features/practice/hooks/useCreatePracticeService";
import { useDeletePracticeService } from "features/practice/hooks/useDeletePracticeService";
import { usePracticeServicesAll } from "features/practice/hooks/usePracticeServicesAll";
import { useTogglePracticeService } from "features/practice/hooks/useTogglePracticeService";

import styles from "./ServiceDetailsPage.module.css";
import { useServiceColumns } from "./useServiceColumns";

const SORT_OPTIONS = [
  { label: "Name A–Z", field: "name", direction: "asc" },
  { label: "Name Z–A", field: "name", direction: "desc" },
  { label: "Price Low–High", field: "price", direction: "asc" },
  { label: "Price High–Low", field: "price", direction: "desc" },
];

const FILTERS_CONFIG = [
  {
    id: "status",
    label: "Status",
    type: "single",
    options: [
      { value: "enabled", label: "Enabled" },
      { value: "disabled", label: "Disabled" },
    ],
  },
];

export const ServiceDetailsPage = () => {
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState(null);
  const [filters, setFilters] = useState({});

  const tenantVal = localStorage.getItem("kinesin-tenant");

  const { data: services = [], isLoading } = usePracticeServicesAll();
  const { mutate: createService } = useCreatePracticeService();
  const { mutate: deleteService } = useDeletePracticeService();
  const { mutate: toggleService } = useTogglePracticeService();

  const processedServices = useMemo(() => {
    let result = [...services];

    const statusFilter = filters.status;

    if (statusFilter === "enabled") {
      result = result.filter((s) => !s.disabled);
    } else if (statusFilter === "disabled") {
      result = result.filter((s) => s.disabled);
    }

    if (searchValue) {
      result = result.filter((s) =>
        s.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (sort) {
      result.sort((a, b) => {
        const aValue = sort.field === "price" ? a.price : a.name?.toLowerCase();
        const bValue = sort.field === "price" ? b.price : b.name?.toLowerCase();

        if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;

        return 0;
      });
    }

    return result;
  }, [services, searchValue, sort, filters]);

  const handleAddTask = () => {
    setSelectedTaskId(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (id) => {
    setSelectedTaskId(id);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setServiceError(null);
  };

  const createNewService = (formValues) => {
    const isEditing = !!selectedTaskId;
    const payload = {
      ...formValues,
      practiceId: tenantVal,
      tenant: tenantVal,
      ...(isEditing ? { id: selectedTaskId } : {}),
    };

    createService(payload, {
      onSuccess: () => {
        toast.success(
          isEditing ? "Service updated successfully" : "New service created successfully"
        );
        setServiceError(null);
        setIsModalOpen(false);
      },
      onError: (error) => {
        toast.error(error.message);
        const message =
          error?.response?.data?.message ||
          "An error occurred while creating the service.";
        setServiceError(message);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;

    deleteService(deleteTarget.id, {
      onSuccess: () => toast.success("Service deleted successfully."),
      onError: (error) => toast.error(error.message),
    });
    setDeleteTarget(null);
  };

  const handleToggleConfirm = () => {
    if (!toggleTarget) return;

    toggleService(
      { id: toggleTarget.id, disabled: toggleTarget.disabled },
      {
        onSuccess: () =>
          toast.success(
            toggleTarget.disabled ? "Service enabled." : "Service disabled."
          ),
        onError: () =>
          toast.error("An error occurred while updating service status."),
      }
    );
    setToggleTarget(null);
  };

  const handleSortChange = (field, direction) => {
    setSort({ field, direction });
  };

  const handleFilterChange = (groupId, value) => {
    setFilters((previous) => ({ ...previous, [groupId]: value }));
  };

  const { columns, rowActions } = useServiceColumns({
    onEdit: handleEditTask,
    onDelete: setDeleteTarget,
    onToggleClick: setToggleTarget,
  });

  const editService = services.find((s) => s.id === selectedTaskId) ?? null;

  return (
    <Box className={styles.content}>
      <PageHeader title="Practice Services">
        <Button
          variant="text"
          endIcon={Plus}
          data-testid="add-service-button"
          onClick={handleAddTask}
        >
          Add Services
        </Button>
      </PageHeader>
      <DataTable
        columns={columns}
        data={processedServices}
        isLoading={isLoading}
        emptyTitle="No services found"
        emptyText="There are no services to display."
        rowsPerPage={10}
        rowActions={rowActions}
        search={{
          value: searchValue,
          onChange: setSearchValue,
          placeholder: "Search by name",
        }}
        sort={sort}
        sortOptions={SORT_OPTIONS}
        onSortChange={handleSortChange}
        filtersConfig={FILTERS_CONFIG}
        filters={{ value: filters, onChange: handleFilterChange }}
      />
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Service"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmVariant="primary"
      />
      <ConfirmDialog
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={handleToggleConfirm}
        title={toggleTarget?.disabled ? "Enable Service" : "Disable Service"}
        message={`Are you sure you want to ${
          toggleTarget?.disabled ? "enable" : "disable"
        } "${toggleTarget?.name}"?`}
        confirmLabel={toggleTarget?.disabled ? "Enable" : "Disable"}
      />
      <ServiceModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={createNewService}
        editService={editService}
        error={serviceError}
        setError={setServiceError}
      />
    </Box>
  );
};
